import React, { useState, useEffect, useCallback, useRef } from 'react'
import './App.css'
import './Home.css' // ticket card + grid styles (loaded on top of the Feepage stylesheet by Home)
import './HomePager.css' // shared pagination styles
import axios from 'axios'
import TopBarProgress from 'react-topbar-progress-indicator'
import { API_URL } from './config'
import { BiSolidLocationPlus } from 'react-icons/bi'
import { BsArrowUpRight, BsClock, BsTicketPerforated } from 'react-icons/bs'

const WS = `${API_URL}/apps/kscanner/webservices.php`

const PAGE_SIZE = 15

// [1, '…', 4, 5, 6, '…', 12] style page list.
const pageList = (page, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const out = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(total - 1, page + 1)
  if (start > 2) out.push('…')
  for (let n = start; n <= end; n++) out.push(n)
  if (end < total - 1) out.push('…')
  out.push(total)
  return out
}

const MONTHS = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
}

// Turns "25 Apr 2026" (or "25 April 2026", or "2026-04-25") into "2026-04-25". Returns '' if unreadable.
const toISODate = (value) => {
  const s = String(value || '').trim()
  if (!s) return ''
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const m = /(\d{1,2})\s+([A-Za-z]{3})[A-Za-z]*\.?,?\s+(\d{4})/.exec(s)
  if (m) {
    const mon = MONTHS[m[2].toLowerCase()]
    if (mon) return `${m[3]}-${mon}-${String(m[1]).padStart(2, '0')}`
  }
  return ''
}

// Descriptions come back as HTML; card previews only need plain text.
const plainText = (html = '', max = 90) => {
  const clean = String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return clean.length > max ? clean.slice(0, max).trimEnd() + '…' : clean
}

const hidePoster = (e) => {
  e.currentTarget.style.display = 'none'
}

// Cursor-follow spotlight on cards.
const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`)
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`)
}

const IndexedEvents = ({ limit = null }) => {
  const [province] = useState('')
  const [currentEvent, setCurrentEvent] = useState([])
  const [page, setPage] = useState(1)
  const sectionRef = useRef(null)
  const [userData, setUserData] = useState({
    email: '',
    phone: '',
    customername: '',
  })

  const [loading, setLoading] = useState({
    loadBarProgress: true,
    error: null,
  })

  const customerid = localStorage.getItem('customerid')

  const fetchEvents = useCallback(
    (prov) => {
      const queryProv = prov ? `&province=${prov}` : ''
      const apiUrlCur = `${WS}?query=INDEXED-EVENTS${queryProv}`

      fetch(apiUrlCur)
        .then((res) => res.json())
        .then((result) => {
          let events = Array.isArray(result) ? result : []

          if (limit) {
            events = events.slice(0, limit)
          }

          setCurrentEvent(events)
          setPage(1)
          setLoading((prev) => ({
            ...prev,
            loadBarProgress: false,
          }))
        })
        .catch((err) =>
          setLoading((prev) => ({
            ...prev,
            error: err,
            loadBarProgress: false,
          })),
        )
    },
    [limit],
  )

  useEffect(() => {
    if (customerid) {
      axios
        .get(`${WS}?query=CUSTOMERDETAILS&accountid=${customerid}`, {
          withCredentials: true,
        })
        .then((response) => {
          setUserData({
            email: response.data.email,
            phone: response.data.phone,
            customername: response.data.customername,
          })
        })
        .catch((err) => console.error('User fetch error:', err))
    }

    fetchEvents(province)
  }, [province, fetchEvents, customerid])

  const goToPage = (n) => {
    setPage(n)
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const date = new Date()

  const fulldate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`

  if (loading.error) {
    return (
      <div className="fp-state" role="alert">
        <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
        <h3>We couldn't load external events</h3>
        <p>Check your connection and try again.</p>
      </div>
    )
  }

  // Hide events that have already passed, and upcoming events whose balance is below 3.5.
  // External events only carry `sdate` (e.g. "25 Apr 2026"), so that is the date we check.
  const visibleEvents = currentEvent.filter((item) => {
    const day = toISODate(item.sdate) || toISODate(item.event_day)
    const isPast = Boolean(day) && day < fulldate
    const lowBalance = item.balance < 3.5 && day > fulldate
    return !isPast && !lowBalance
  })

  // pagination: 15 per page, controls only when there are more than 15
  const totalPages = Math.max(1, Math.ceil(visibleEvents.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pagedEvents = visibleEvents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const showPager = visibleEvents.length > PAGE_SIZE

  return (
    <section className="fp-indexed" ref={sectionRef}>
      <div className="fp-head">
        <h2 className="fp-h2">
          More events from <span>around the web.</span>
        </h2>

        <div>
          <p>Discover events listed on Karamale but ticketed through external platforms.</p>

          {!loading.loadBarProgress && visibleEvents.length > 0 && (
            <span className="fp-count">
              {visibleEvents.length} {visibleEvents.length === 1 ? 'event' : 'events'}
            </span>
          )}
        </div>
      </div>

      {loading.loadBarProgress && (
        <>
          <TopBarProgress />

          <div className="fp-egrid" aria-busy="true" aria-label="Loading external events">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="fp-skel" key={i}>
                <div className="fp-skel__img" />
                <div className="fp-skel__body">
                  <div className="fp-skel__line" />
                  <div className="fp-skel__line" />
                  <div className="fp-skel__line" />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading.loadBarProgress && visibleEvents.length === 0 && (
        <div className="fp-state">
          <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
          <h3>No external events found</h3>
          <p>Check back soon for more events from around the web.</p>
        </div>
      )}

      {!loading.loadBarProgress && visibleEvents.length > 0 && (
        <div className="fp-egrid">
          {pagedEvents.map((item, index) => (
            <div className="fp-cell" key={item.eventid || index}>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="fp-event fp-event--external"
                aria-label={`${item.eventname}, external event`}
                onMouseMove={spot}
              >
                <div className="fp-event__bg">
                  <BsTicketPerforated aria-hidden="true" />
                  <img src={item.poster} alt="" loading="lazy" onError={hidePoster} />
                </div>
                <div className="fp-event__shade" aria-hidden="true" />

                <span className="fp-event__cat">
                  <BsArrowUpRight aria-hidden="true" /> External event
                </span>

                <div className="fp-event__info">
                  <h3 className="fp-event__title">{item.eventname}</h3>
                  <p className="fp-event__place">
                    <BiSolidLocationPlus aria-hidden="true" />
                    <span>{item.location}</span>
                  </p>
                  <p className="fp-event__desc">{plainText(item.description)}</p>
                </div>

                <div className="fp-event__stub">
                  <div className="fp-event__when">
                    <strong>{item.sdate}</strong>
                    {item.stime && (
                      <span>
                        <BsClock aria-hidden="true" /> {item.stime}
                      </span>
                    )}
                  </div>

                  <span className="fp-event__cta">
                    View event <BsArrowUpRight aria-hidden="true" />
                  </span>
                </div>
              </a>
            </div>
          ))}
        </div>
      )}

      {!loading.loadBarProgress && showPager && (
        <nav className="fp-pager" aria-label="External events pagination">
          <button
            type="button"
            className="fp-pager__btn"
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            Previous
          </button>

          <ul className="fp-pager__list">
            {pageList(currentPage, totalPages).map((n, i) =>
              n === '…' ? (
                <li key={`gap-${i}`} className="fp-pager__gap" aria-hidden="true">
                  …
                </li>
              ) : (
                <li key={n}>
                  <button
                    type="button"
                    className={`fp-pager__num ${n === currentPage ? 'is-active' : ''}`}
                    aria-current={n === currentPage ? 'page' : undefined}
                    aria-label={`Page ${n}`}
                    onClick={() => goToPage(n)}
                  >
                    {n}
                  </button>
                </li>
              ),
            )}
          </ul>

          <button
            type="button"
            className="fp-pager__btn"
            disabled={currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </section>
  )
}

export default IndexedEvents
